import { useMemo, useState } from 'react';
import { usePredictionMarketEventsFindLobby, usePredictionMarketCategories } from '@/lib/endpoints';
import { useAppSearchParams } from '@/hooks/useAppSearchParams';
import { useHiddenCategories } from '@/lib/territory/useTerritory';
import { cn } from '@/lib/utils';
import { SectionHeader, Skeleton } from '@/components/ui';
import type { OrderSide } from '@/analytics';
import type { EventCardDto, MarketOptionDto } from '@/lib/models';
import { BinaryQuestionCard } from '../BinaryMarkets/BinaryQuestionCard';
import { BinaryQuestionCardSkeleton } from '../BinaryMarkets/BinaryQuestionCardSkeleton';
import { BuyInPopup, type BuyMarketContext } from '../BinaryMarkets/BuyInPopup';
import { SportsMoneylineCard } from '../SportsMarkets/SportsMoneylineCard';

/** Popup target carrying the resolved option context + opening side. */
interface BuyTarget {
  context: BuyMarketContext;
  side: OrderSide;
}

/**
 * How many events each category section previews before "See All".
 * Mobile/tablet render the first two (per the Figma frames); the third only
 * appears at xl, where it completes the three-column desktop row.
 */
const EVENTS_PER_SECTION = 3;
/** One fetch feeds every section; big enough that later categories aren't starved. */
const LOBBY_FETCH_LIMIT = 50;

/**
 * Trending-lobby category sections (Figma 5751-141388): one titled section per
 * category — "Sports", "Science and Technology", … — each previewing up to
 * {@link EVENTS_PER_SECTION} question cards with a "See All" that drills into
 * that category via `?category=` (same URL contract as the tab bar).
 *
 * One unfiltered lobby fetch feeds all sections (grouped client-side by
 * `categoryCode`); section order follows the admin-ordered categories list,
 * with territory-hidden categories dropped. Owns the shared buy-in popup for
 * every card it renders.
 */
export function CategorySections() {
  const { setCategory } = useAppSearchParams();
  const { data, isLoading, isError } = usePredictionMarketEventsFindLobby({ limit: LOBBY_FETCH_LIMIT });
  const { data: categories } = usePredictionMarketCategories();
  const hiddenCategories = useHiddenCategories();
  const [buyTarget, setBuyTarget] = useState<BuyTarget | null>(null);

  const sections = useMemo(() => {
    const events = data?.events ?? [];
    const byCategory = new Map<string, EventCardDto[]>();
    for (const event of events) {
      const group = byCategory.get(event.categoryCode);
      if (group) group.push(event);
      else byCategory.set(event.categoryCode, [event]);
    }
    return (categories ?? [])
      .filter((cat) => !hiddenCategories.includes(cat.code.toUpperCase()))
      .map((cat) => ({
        code: cat.code,
        title: cat.displayName ?? cat.code,
        events: (byCategory.get(cat.code) ?? []).slice(0, EVENTS_PER_SECTION),
      }))
      .filter((section) => section.events.length > 0);
  }, [data, categories, hiddenCategories]);

  function handleOpenBuy(event: EventCardDto, option: MarketOptionDto, side: OrderSide) {
    setBuyTarget({ context: { ...option, eventName: event.name }, side });
  }

  if (isError) {
    return <div className="w-full py-6 text-center text-sm text-muted">Failed to load markets.</div>;
  }

  if (isLoading) {
    // Mirrors the loaded layout — titled sections with the responsive card
    // grid — so nothing reflows when the real sections land.
    return (
      <div className="flex w-full flex-col gap-4">
        {Array.from({ length: 2 }).map((_, s) => (
          <section key={s} className="flex w-full flex-col gap-2.5">
            <div className="flex items-center justify-between">
              <Skeleton className="h-6 w-36 rounded" />
              <Skeleton className="h-4 w-14 rounded" />
            </div>
            <div className="flex flex-col gap-2.5 md:grid md:grid-cols-2 md:items-stretch md:gap-4 xl:grid-cols-3">
              {Array.from({ length: EVENTS_PER_SECTION }).map((_, i) => (
                <div key={i} className={cn('h-full', i >= 2 && 'hidden xl:block')}>
                  <BinaryQuestionCardSkeleton />
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    );
  }

  if (sections.length === 0) {
    return <div className="w-full py-6 text-center text-sm text-muted">No markets available yet.</div>;
  }

  return (
    <div className="flex w-full flex-col gap-4">
      {sections.map((section) => (
        <section key={section.code} className="flex w-full flex-col gap-2.5">
          <SectionHeader title={section.title} onSeeAll={() => setCategory(section.code)} />
          <div className="flex flex-col gap-2.5 md:grid md:grid-cols-2 md:items-stretch md:gap-4 xl:grid-cols-3">
            {section.events.map((event, i) => (
              <div key={event.id} className={cn('h-full', i >= 2 && 'hidden xl:block')}>
                {event.sportsMoneyline ? (
                  <SportsMoneylineCard
                    event={event}
                    moneyline={event.sportsMoneyline}
                    onOpenBuy={(option, side) => handleOpenBuy(event, option, side)}
                  />
                ) : (
                  <BinaryQuestionCard event={event} onOpenBuy={(option, side) => handleOpenBuy(event, option, side)} />
                )}
              </div>
            ))}
          </div>
        </section>
      ))}

      <BuyInPopup
        market={buyTarget?.context ?? null}
        initialSide={buyTarget?.side ?? 'yes'}
        surface="home.card"
        onClose={() => setBuyTarget(null)}
      />
    </div>
  );
}
